import { atom, selector, selectorFamily } from "recoil";

import { endOfDay, parse, startOfDay } from "date-fns";
import { ForecastRequest, ForecastResult, ProposalRequestLite } from "./api";
import {
  fetchCalculate,
  fetchForecastConversionGroups,
  fetchForecasts,
  fetchPackages,
  fetchProposal,
  fetchSalesPeriod,
  fetchSpotLengths,
} from "./apiWrappers";
import { AppSettings, Medium, Order } from "./types";
import { getQuery } from "./utils";

export type Result = "success" | "error";

export const appSettingsState = atom<AppSettings>({
  key: "appSettings",
  default: {
    homeUrl: window.location.pathname ?? "/portalLite",
    redirectTo: window.location.pathname ?? "/portalLite",
    text: "",
    buttonText: "Bereken",
    buttonStyle: "tertiary",
  },
});

export const queryState = atom({ key: "query", default: getQuery() });

export const orderState = atom({
  key: "order",
  default: selector({
    key: "initialData",
    get: ({ get }) => {
      // Als er geen redirect url is wordt er ook geen order gevuld
      const appSettings = get(appSettingsState);
      if (!appSettings.homeUrl) {
        return null;
      }

      const query = get(queryState);
      const medium = query.get("medium") as Medium;
      const fromString = query.get("from");
      const from = fromString
        ? startOfDay(parse(fromString, "yyyy-MM-dd", new Date()))
        : undefined;
      const toString = query.get("to");
      const to = toString
        ? endOfDay(parse(toString, "yyyy-MM-dd", new Date()))
        : undefined;
      const budget = Number(query.get("budget"));
      const packageCode = query.get("packageCode");
      const targetGroupIndex = query.get("targetGroupIndex");

      if (
        !medium ||
        !from ||
        !to ||
        !budget ||
        !packageCode ||
        !targetGroupIndex
      ) {
        return null;
      }

      const packageRequest = {
        medium,
        monthNumber: from.getMonth() + 1,
        year: from.getFullYear(),
      };
      const packages = get(packagesState(packageRequest));
      const targetGroup = packages.packages
        .find((s) => s.targetGroups.find((t) => t.packageCode === packageCode))
        ?.targetGroups.find((t) => t.index === Number(targetGroupIndex));

      return {
        medium,
        period: [from, to],
        subOrders: [
          {
            id: 1,
            period: [from, to],
            budget,
            packageCode,
            targetGroupId: targetGroup?.targetGroupId ?? "",
            targetGroupIndex: Number(targetGroupIndex),
            spotLength: 20,
            channels: targetGroup?.channels ?? [],
          },
        ],
        forecastConversionGroups: [],
      } as Order;
    },
  }),
});

export const packagesState = selectorFamily({
  key: "packages",
  get: (request: { medium: Medium; monthNumber: number; year: number }) => () =>
    fetchPackages(request),
});

/**
 * ID of the last added suborder
 */
export const lastIdState = atom({
  key: "lastId",
  default: 1,
});

export const spotLengthsState = selectorFamily({
  key: "spotLengths",
  get: (request: { medium: Medium; year: number }) => () =>
    fetchSpotLengths(request),
});

export const calculateState = selectorFamily({
  key: "calculate",
  get:
    (input: {
      medium: Medium;
      calculateInput: {
        dateFrom: Date;
        dateTo: Date;
        packageCode: string;
        targetGroupId: string;
        spotLength: number;
        budget: number;
        channels: Array<string>;
        calculated?: never;
      };
    }) =>
    () =>
      fetchCalculate(input),
});

const emptyForecasts: ForecastResult = {
  state: "error",
  unknownPackageCodes: [],
};

// The Recoil types for `selectorFamily` have a bug in which they do not
// allow interfaces to be passed in as a parameter.
// see: https://github.com/facebookexperimental/Recoil/issues/629#issuecomment-914273925
type CreateSerializableParam<Type> = {
  // We construct a new mapped type in which the keys are the keys of the original type and the values are
  // converted from interface types into normal types (which appears to happen implicitly when using mapped types).
  //
  // When the type of a property is already a `SerializableParam` we return it as-is.
  // But if it is not, we should check to see if it it is a record/object or undefined and then recursively map it.
  // Finally, if it matches neither of these conditions we should return it as-is.
  [Property in keyof Type]: Type[Property] extends import("recoil").SerializableParam
    ? Type[Property]
    : // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Type[Property] extends Record<string, any> | undefined | null
      ? CreateSerializableParam<Type[Property]>
      : Type[Property];
};

export const forecastsState = selectorFamily({
  key: "forecasts",
  get:
    (input: {
      forecastRequest: CreateSerializableParam<ForecastRequest>;
      medium: Medium;
    }) =>
    () => {
      if (
        !input.forecastRequest.subOrders ||
        input.forecastRequest.subOrders.length === 0
      ) {
        return emptyForecasts;
      }

      return fetchForecasts(input)
        .then((result) => ({ ...result, state: "success" }) as ForecastResult)
        .catch(() => emptyForecasts);
    },
});

export const proposalState = atom<ProposalRequestLite | null>({
  key: "proposal",
  default: null,
});

export const proposalRequestState = selector({
  key: "proposalRequest",
  get: ({ get }): Promise<Result | undefined> => {
    const input = get(proposalState);
    if (!input) {
      return Promise.resolve(undefined);
    }

    return fetchProposal({
      proposalRequestLite: input as ProposalRequestLite,
    })
      .then(() => "success" as Result)
      .catch(() => "error" as Result);
  },
});

export const forecastConversionGroupsState = selectorFamily({
  key: "forecastConversionGroups",
  get: (medium: Medium) => () => fetchForecastConversionGroups(medium),
});

export const salesPeriodsState = selector({
  key: "salesPeriods",
  get: fetchSalesPeriod,
});
